import {
  Badge,
  Group,
  Paper,
  Text,
  Tooltip,
  ActionIcon,
  Stack,
} from "@mantine/core";
import { IconFileText, IconCircleOff } from "@tabler/icons-react";
import { useCallback, useMemo } from "react";
import { Case } from "../../services/cases.service";
import { formatDateOnlyForDisplay, parseDateOnly } from "../../utils/dateOnly";

interface HistoricalReportCardProps {
  caseData: Case;
  onViewReport: (caseId: string, caseNumber: string) => void;
}

export function HistoricalReportCard({
  caseData,
  onViewReport,
}: HistoricalReportCardProps) {
  const isOriginalReport = useMemo(
    () => Boolean(caseData.isLegacyImported),
    [caseData.isLegacyImported]
  );
  const canViewReport = useMemo(
    () => isOriginalReport || Boolean(caseData.hasReportPdf),
    [caseData.hasReportPdf, isOriginalReport]
  );
  const reportLabel = useMemo(
    () => (isOriginalReport ? "View original report PDF" : "View Report PDF"),
    [isOriginalReport]
  );
  const receivedDate = useMemo(() => {
    const dateReceived = parseDateOnly(caseData.dateReceived);
    return dateReceived ? formatDateOnlyForDisplay(dateReceived) : undefined;
  }, [caseData.dateReceived]);

  const handleClick = useCallback(() => {
    if (canViewReport) {
      onViewReport(caseData.id, caseData.caseNumber);
    }
  }, [caseData.id, caseData.caseNumber, canViewReport, onViewReport]);

  const handleIconClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handleClick();
    },
    [handleClick]
  );

  return (
    <Paper
      p="xs"
      withBorder
      styles={{
        root: {
          cursor: canViewReport ? "pointer" : "default",
          backgroundColor: canViewReport
            ? undefined
            : "var(--mantine-color-gray-0)",
        },
      }}
      onClick={handleClick}
    >
      <Group justify="space-between" wrap="nowrap">
        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" wrap="nowrap">
            <Text size="xs" fw={600} truncate>
              {caseData.caseNumber}
            </Text>
            {isOriginalReport ? (
              <Badge size="xs" variant="light" color="blue">
                Original report
              </Badge>
            ) : canViewReport ? (
              <Badge size="xs" variant="filled" color="green">
                Ready
              </Badge>
            ) : (
              <Badge size="xs" variant="light" color="gray">
                Unavailable
              </Badge>
            )}
          </Group>
          <Text size="xs" c="dimmed" truncate>
            {caseData.procedure?.name || "No procedure"}
          </Text>
          {receivedDate && (
            <Text size="xs" c="dimmed">
              {receivedDate}
            </Text>
          )}
        </Stack>

        <Tooltip
          label={canViewReport ? reportLabel : "Report PDF not available"}
          position="left"
        >
          <ActionIcon
            variant="subtle"
            color={canViewReport ? "blue" : "gray"}
            aria-label={
              canViewReport ? reportLabel : "Report PDF not available"
            }
            disabled={!canViewReport}
            onClick={handleIconClick}
          >
            {canViewReport ? (
              <IconFileText size={16} />
            ) : (
              <IconCircleOff size={16} />
            )}
          </ActionIcon>
        </Tooltip>
      </Group>
    </Paper>
  );
}
